import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import { HomeOutlined, FileSearchOutlined, ArrowLeftOutlined } from '@ant-design/icons';

class Page404 extends React.Component {
	constructor(props) {
		super(props);
		this.state = { isMobile: window.innerWidth <= 768 };
	}

	componentDidMount() {
		this._handleResize = () => this.setState({ isMobile: window.innerWidth <= 768 });
		window.addEventListener('resize', this._handleResize);
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this._handleResize);
	}

	render() {
		const { isMobile } = this.state;

		const css = `
			@keyframes floatIcon {
				0%, 100% { transform: translateY(0px); }
				50%       { transform: translateY(-16px); }
			}
			@keyframes fadeUp {
				from { opacity: 0; transform: translateY(28px); }
				to   { opacity: 1; transform: translateY(0); }
			}
			@keyframes scaleIn {
				from { opacity: 0; transform: scale(0.85); }
				to   { opacity: 1; transform: scale(1); }
			}
			@keyframes rotateSlow {
				from { transform: rotate(0deg); }
				to   { transform: rotate(360deg); }
			}
			.p404-icon  { animation: floatIcon 3.5s ease-in-out infinite; }
			.p404-a1    { animation: scaleIn 0.5s ease-out 0.05s both; }
			.p404-a2    { animation: fadeUp  0.5s ease-out 0.15s both; }
			.p404-a3    { animation: fadeUp  0.5s ease-out 0.28s both; }
			.p404-a4    { animation: fadeUp  0.5s ease-out 0.42s both; }
			.p404-ring  { animation: rotateSlow 12s linear infinite; }
			.p404-btn-primary:hover {
				transform: translateY(-2px);
				box-shadow: 0 12px 32px rgba(15,40,71,0.35) !important;
				transition: all 0.2s ease;
			}
			.p404-btn-ghost:hover {
				background: rgba(15,40,71,0.06) !important;
				transform: translateY(-1px);
				transition: all 0.2s ease;
			}
		`;

		/* background decorative blobs */
		const decorBlobs = (
			<>
				<div style={{
					position: 'absolute', top: isMobile ? -60 : -80, right: isMobile ? -60 : -40,
					width: isMobile ? 220 : 320, height: isMobile ? 220 : 320,
					borderRadius: '50%',
					background: 'radial-gradient(circle, rgba(30,77,138,0.10) 0%, transparent 70%)',
					pointerEvents: 'none',
				}} />
				<div style={{
					position: 'absolute', bottom: isMobile ? -40 : -60, left: isMobile ? -40 : -60,
					width: isMobile ? 180 : 280, height: isMobile ? 180 : 280,
					borderRadius: '50%',
					background: 'radial-gradient(circle, rgba(15,40,71,0.08) 0%, transparent 70%)',
					pointerEvents: 'none',
				}} />
			</>
		);

		/* rotating ring behind icon */
		const ringDecor = (size) => (
			<svg
				className="p404-ring"
				width={size} height={size}
				viewBox={`0 0 ${size} ${size}`}
				style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}
			>
				<circle
					cx={size / 2} cy={size / 2} r={size / 2 - 4}
					fill="none"
					stroke="rgba(30,77,138,0.18)"
					strokeWidth="2"
					strokeDasharray="12 8"
					strokeLinecap="round"
				/>
			</svg>
		);

		/* icon block */
		const iconSize = isMobile ? 100 : 140;
		const ringSize = isMobile ? 148 : 200;
		const iconBlock = (
			<div style={{ position: 'relative', width: ringSize, height: ringSize, flexShrink: 0 }}>
				{ringDecor(ringSize)}
				<div
					className="p404-icon"
					style={{
						position       : 'absolute',
						top            : '50%', left: '50%',
						transform      : 'translate(-50%, -50%)',
						width          : iconSize, height: iconSize,
						borderRadius   : '28%',
						background     : 'linear-gradient(135deg, #0f2847 0%, #1e4d8a 100%)',
						display        : 'flex', alignItems: 'center', justifyContent: 'center',
						boxShadow      : '0 16px 48px rgba(15,40,71,0.28), 0 4px 16px rgba(15,40,71,0.18)',
					}}
				>
					<FileSearchOutlined style={{ fontSize: iconSize * 0.44, color: '#fff' }} />
				</div>
			</div>
		);

		/* 404 number — tiga digit terpisah agar gradient tidak bermasalah */
		const numStyle = (delay) => ({
			display   : 'inline-block',
			fontSize  : isMobile ? 88 : 120,
			fontWeight: 900,
			lineHeight: 1,
			color     : '#0f2847',
			letterSpacing: -2,
			animation : `fadeUp 0.5s ease-out ${delay}s both`,
			userSelect: 'none',
		});

		const numBlock = (
			<div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 8, marginBottom: isMobile ? 16 : 20 }}>
				<span style={numStyle(0.05)}>4</span>
				<span style={{
					...numStyle(0.15),
					color           : 'transparent',
					WebkitTextStroke: isMobile ? '3px #1e4d8a' : '4px #1e4d8a',
				}}>
					0
				</span>
				<span style={numStyle(0.25)}>4</span>
			</div>
		);

		/* text content */
		const textBlock = (
			<div className="p404-a3" style={{ marginBottom: isMobile ? 28 : 32 }}>
				<div style={{
					fontSize  : isMobile ? 20 : 24,
					fontWeight: 700,
					color     : '#0f2847',
					marginBottom: 10,
					lineHeight: 1.3,
				}}>
					Halaman Tidak Ditemukan
				</div>
				<div style={{
					fontSize  : isMobile ? 14 : 15,
					color     : '#6b7280',
					lineHeight: 1.75,
					maxWidth  : 360,
				}}>
					Halaman yang kamu cari tidak tersedia atau mungkin sudah dipindahkan.
					<br />
					Silakan kembali ke dashboard.
				</div>
			</div>
		);

		/* action buttons */
		const buttons = (
			<div className="p404-a4" style={{
				display      : 'flex',
				flexDirection: isMobile ? 'column' : 'row',
				gap          : 12,
				alignItems   : isMobile ? 'stretch' : 'center',
			}}>
				<Link to="/admin" style={isMobile ? { display: 'block' } : {}}>
					<Button
						type="primary"
						size="large"
						icon={<HomeOutlined />}
						className="p404-btn-primary"
						style={{
							borderRadius: 10,
							background  : 'linear-gradient(135deg, #0f2847, #1e4d8a)',
							border      : 'none',
							fontWeight  : 700,
							height      : 46,
							padding     : '0 28px',
							fontSize    : 14,
							boxShadow   : '0 8px 24px rgba(15,40,71,0.22)',
							width       : isMobile ? '100%' : 'auto',
						}}
					>
						Kembali ke Dashboard
					</Button>
				</Link>
				<Button
					size="large"
					icon={<ArrowLeftOutlined />}
					className="p404-btn-ghost"
					onClick={() => window.history.back()}
					style={{
						borderRadius: 10,
						border      : '1.5px solid #d0dae8',
						fontWeight  : 600,
						height      : 46,
						padding     : '0 24px',
						fontSize    : 14,
						color       : '#374151',
						background  : 'transparent',
						width       : isMobile ? '100%' : 'auto',
					}}
				>
					Halaman Sebelumnya
				</Button>
			</div>
		);

		/* divider line */
		const divider = (
			<div style={{
				width       : 48,
				height      : 3,
				borderRadius: 2,
				background  : 'linear-gradient(90deg, #0f2847, #1e4d8a)',
				marginBottom: isMobile ? 20 : 24,
			}} />
		);

		return (
			<div style={{
				minHeight      : 'calc(100vh - 130px)',
				display        : 'flex',
				alignItems     : 'center',
				justifyContent : 'center',
				padding        : isMobile ? '40px 20px' : '60px 48px',
				background     : 'linear-gradient(150deg, #eef3fc 0%, #f8faff 45%, #fff 100%)',
				position       : 'relative',
				overflow       : 'hidden',
			}}>
				<style>{css}</style>
				{decorBlobs}

				{isMobile ? (
					/* ── MOBILE: stacked ── */
					<div style={{ textAlign: 'center', maxWidth: 340, width: '100%', zIndex: 1 }}>
						<div className="p404-a1" style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
							{iconBlock}
						</div>
						<div style={{ display: 'flex', justifyContent: 'center' }}>
							{numBlock}
						</div>
						{divider}
						{textBlock}
						{buttons}
					</div>
				) : (
					/* ── DESKTOP: horizontal ── */
					<div style={{
						display    : 'flex',
						alignItems : 'center',
						gap        : 80,
						maxWidth   : 900,
						width      : '100%',
						zIndex     : 1,
					}}>
						<div className="p404-a1">
							{iconBlock}
						</div>

						<div style={{ flex: 1, minWidth: 0 }}>
							<div className="p404-a2">
								{numBlock}
							</div>
							{divider}
							{textBlock}
							{buttons}
						</div>
					</div>
				)}
			</div>
		);
	}
}

export default Page404;
